// ONBW1-S3 (docs/build-briefs/ONBW1-S3-owner-wizard.md; design/mockups/onboarding-wizard/). The small drawing pieces the location
// owner's setup wizard is made of: elements, choices, tags, the number pad sheet and the two drawings (the card reader and the
// receipt printer). No state of its own and no network. All text goes in through textContent, never as markup.

import { padAppend, padDelete } from "./ownerWizardModel.ts";

type Child = HTMLElement | SVGElement | string;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  options: { cls?: string; text?: string; testid?: string; attrs?: Record<string, string> } = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (options.cls !== undefined) node.className = options.cls;
  if (options.text !== undefined) node.textContent = options.text;
  if (options.testid !== undefined) node.dataset["testid"] = options.testid;
  for (const [name, value] of Object.entries(options.attrs ?? {})) node.setAttribute(name, value);
  for (const child of children) node.append(child);
  return node;
}

export function button(
  text: string,
  testid: string,
  cls: string,
  onClick: () => void,
): HTMLButtonElement {
  const node = el("button", { cls, text, testid, attrs: { type: "button" } });
  node.addEventListener("click", onClick);
  return node;
}

export function tag(
  text: string,
  tone: "ok" | "warn" | "info" | "todo",
  testid?: string,
): HTMLElement {
  return el("span", {
    cls: `ow-tag ow-tag-${tone}`,
    text,
    ...(testid === undefined ? {} : { testid }),
  });
}

/** A labelled field: the label is tied to the control by id. */
export function field(label: string, control: HTMLElement, cls = ""): HTMLElement {
  const labelEl = el("label", { cls: "ow-label", text: label });
  if (control.id !== "") labelEl.htmlFor = control.id;
  return el("div", { cls: `ow-field ${cls}`.trim() }, labelEl, control);
}

export function textInput(options: {
  id: string;
  testid: string;
  value: string;
  inputmode?: string;
  autocomplete?: string;
  autocapitalize?: string;
  maxlength?: number;
  placeholder?: string;
}): HTMLInputElement {
  const input = el("input", {
    cls: "ow-input",
    testid: options.testid,
    attrs: {
      id: options.id,
      type: "text",
      inputmode: options.inputmode ?? "text",
      autocomplete: options.autocomplete ?? "off",
      autocapitalize: options.autocapitalize ?? "words",
      ...(options.maxlength === undefined ? {} : { maxlength: String(options.maxlength) }),
      ...(options.placeholder === undefined ? {} : { placeholder: options.placeholder }),
    },
  });
  input.value = options.value;
  return input;
}

export function selectInput(
  id: string,
  testid: string,
  choices: ReadonlyArray<{ value: string; label: string }>,
  value: string,
  placeholder: string,
): HTMLSelectElement {
  const select = el("select", { cls: "ow-input ow-select", testid, attrs: { id } });
  const none = el("option", { text: placeholder, attrs: { value: "" } });
  none.disabled = true;
  select.append(none);
  for (const choice of choices) {
    select.append(el("option", { text: choice.label, attrs: { value: choice.value } }));
  }
  select.value = value;
  return select;
}

/** A row of buttons of which one is chosen: aria-pressed marks it. `onPick` runs on a tap. */
export function chipRow(
  testid: string,
  chipTestid: string,
  choices: ReadonlyArray<{ value: string; label: string }>,
  chosen: string,
  onPick: (value: string) => void,
  ariaLabel?: string,
): HTMLElement {
  const row = el("div", {
    cls: "ow-chips",
    testid,
    attrs: { role: "group", ...(ariaLabel === undefined ? {} : { "aria-label": ariaLabel }) },
  });
  for (const choice of choices) {
    const chip = el("button", {
      cls: "ow-chip",
      text: choice.label,
      testid: chipTestid,
      attrs: {
        type: "button",
        "aria-pressed": choice.value === chosen ? "true" : "false",
        "data-value": choice.value,
      },
    });
    chip.addEventListener("click", () => onPick(choice.value));
    row.append(chip);
  }
  return row;
}

/** Paints which chip of a chip row is chosen, without redrawing the row. */
export function markChosen(row: HTMLElement, chosen: string): void {
  for (const chip of row.querySelectorAll<HTMLButtonElement>("button[data-value]")) {
    chip.setAttribute("aria-pressed", chip.dataset["value"] === chosen ? "true" : "false");
  }
}

export function card(testid: string | undefined, ...children: Child[]): HTMLElement {
  return el("div", { cls: "ow-card", ...(testid === undefined ? {} : { testid }) }, ...children);
}

/** A big choice with a radio mark, for "pick one" questions. */
export function pickCard(options: {
  testid: string;
  title: string;
  chosen: boolean;
  glyph: string;
  onPick: () => void;
}): HTMLButtonElement {
  const node = el(
    "button",
    {
      cls: "ow-pick",
      testid: options.testid,
      attrs: { type: "button", role: "radio", "aria-checked": options.chosen ? "true" : "false" },
    },
    el("span", { cls: "ow-pick-ico", text: options.glyph, attrs: { "aria-hidden": "true" } }),
    el("span", { cls: "ow-pick-title", text: options.title }),
    el("span", { cls: "ow-radio", attrs: { "aria-hidden": "true" } }),
  );
  node.addEventListener("click", options.onPick);
  return node;
}

/** A minus, a value and a plus. The value is a button too, so a tap on it can open the number pad. */
export function stepper(options: {
  testid: string;
  value: string;
  onMinus: () => void;
  onPlus: () => void;
  onValue?: () => void;
  minusDisabled?: boolean;
  plusDisabled?: boolean;
  minusLabel: string;
  plusLabel: string;
}): HTMLElement {
  const minus = el("button", {
    cls: "ow-step",
    text: "−",
    testid: `${options.testid}-minus`,
    attrs: { type: "button", "aria-label": options.minusLabel },
  });
  minus.disabled = options.minusDisabled === true;
  minus.addEventListener("click", options.onMinus);
  const plus = el("button", {
    cls: "ow-step",
    text: "+",
    testid: `${options.testid}-plus`,
    attrs: { type: "button", "aria-label": options.plusLabel },
  });
  plus.disabled = options.plusDisabled === true;
  plus.addEventListener("click", options.onPlus);
  const value = el("button", {
    cls: "ow-stepval",
    text: options.value,
    testid: `${options.testid}-value`,
    attrs: { type: "button" },
  });
  if (options.onValue !== undefined) value.addEventListener("click", options.onValue);
  else value.disabled = true;
  return el("div", { cls: "ow-stepper", testid: options.testid }, minus, value, plus);
}

// ---------------------------------------------------------------------------------------------------
// The number pad sheet
// ---------------------------------------------------------------------------------------------------

/** A sheet over the wizard with a 3 by 4 pad. The number is made from the right, so two decimals are always there: 4, 7, 5
 * reads 4.75. Resolves to the number in hundredths, or undefined when the person cancels. The sheet is its own screen: nothing
 * behind it takes a tap while it is open. */
export function openPadSheet(options: {
  title: string;
  initial: number;
  max: number;
  format: (units: number) => string;
  /** Set while the figure is not allowed; Done stays dimmed with the reason beside it. */
  reasonFor?: (units: number) => string;
  prefix?: string;
}): Promise<number | undefined> {
  return new Promise((resolve) => {
    let value = options.initial;
    const host = document.querySelector<HTMLElement>("#setup-wizard-screen");
    if (host === null) {
      resolve(undefined);
      return;
    }
    const scrim = el("div", { cls: "ow-scrim", testid: "ow-pad-scrim" });
    const display = el("div", {
      cls: "ow-pad-value",
      testid: "ow-pad-value",
      attrs: { "aria-live": "polite" },
    });
    const reason = el("div", { cls: "ow-pad-reason", testid: "ow-pad-reason" });
    const done = el("button", {
      cls: "ow-btn ow-btn-primary",
      text: "Done",
      testid: "ow-pad-done",
      attrs: { type: "button" },
    });
    const finish = (result: number | undefined): void => {
      scrim.remove();
      resolve(result);
    };
    const paint = (): void => {
      display.textContent = options.format(value);
      const why = options.reasonFor?.(value) ?? "";
      reason.textContent = why;
      done.disabled = why !== "";
    };
    const key = (
      label: string,
      testid: string,
      fn: boolean,
      onTap: () => void,
    ): HTMLButtonElement => {
      const node = el("button", {
        cls: fn ? "ow-key ow-key-fn" : "ow-key",
        text: label,
        testid,
        attrs: { type: "button" },
      });
      node.addEventListener("click", onTap);
      return node;
    };
    const keys = el("div", { cls: "ow-keys", testid: "ow-pad-keys" });
    for (const digit of ["1", "2", "3", "4", "5", "6", "7", "8", "9"]) {
      keys.append(
        key(digit, `ow-pad-digit-${digit}`, false, () => {
          value = padAppend(value, digit, options.max);
          paint();
        }),
      );
    }
    keys.append(
      key("Clear", "ow-pad-clear", true, () => {
        value = 0;
        paint();
      }),
      key("0", "ow-pad-digit-0", false, () => {
        value = padAppend(value, "0", options.max);
        paint();
      }),
      key("Delete", "ow-pad-delete", true, () => {
        value = padDelete(value);
        paint();
      }),
    );
    done.addEventListener("click", () => finish(value));
    const cancel = el("button", {
      cls: "ow-btn",
      text: "Cancel",
      testid: "ow-pad-cancel",
      attrs: { type: "button" },
    });
    cancel.addEventListener("click", () => finish(undefined));
    scrim.append(
      el(
        "div",
        {
          cls: "ow-sheet",
          attrs: { role: "dialog", "aria-modal": "true", "aria-label": options.title },
        },
        el("div", { cls: "ow-sheet-title", text: options.title }),
        el(
          "div",
          { cls: "ow-pad-row" },
          ...(options.prefix === undefined
            ? []
            : [el("span", { cls: "ow-pad-prefix", text: options.prefix })]),
          display,
        ),
        reason,
        keys,
        el("div", { cls: "ow-sheet-actions" }, cancel, done),
      ),
    );
    host.append(scrim);
    paint();
  });
}

// ---------------------------------------------------------------------------------------------------
// Drawings. Generic, never any maker's artwork.
// ---------------------------------------------------------------------------------------------------

const SVG_NS = "http://www.w3.org/2000/svg";

function svg(
  tagName: string,
  attrs: Record<string, string>,
  ...children: Array<SVGElement | string>
): SVGElement {
  const node = document.createElementNS(SVG_NS, tagName);
  for (const [name, value] of Object.entries(attrs)) node.setAttribute(name, value);
  for (const child of children) {
    if (typeof child === "string") node.append(child);
    else node.append(child);
  }
  return node;
}

/** The card reader, with the three words it shows picked out and numbered. */
export function readerDrawing(): SVGElement {
  const text = (y: number, value: string): SVGElement =>
    svg(
      "text",
      {
        x: "150",
        y: String(y),
        "text-anchor": "middle",
        "font-weight": "800",
        "font-size": "20",
        fill: "currentColor",
      },
      value,
    );
  return svg(
    "svg",
    {
      viewBox: "0 0 300 300",
      class: "ow-drawing",
      role: "img",
      "aria-label": "A card reader with three words on its screen",
    },
    svg("rect", { x: "70", y: "10", width: "160", height: "280", rx: "24", fill: "#2a211d" }),
    svg("rect", { x: "86", y: "28", width: "128", height: "130", rx: "10", fill: "#f6ead8" }),
    svg(
      "text",
      {
        x: "150",
        y: "50",
        "text-anchor": "middle",
        "font-size": "11",
        "font-weight": "700",
        fill: "#7a4a3a",
      },
      "Pairing code",
    ),
    svg("rect", {
      x: "92",
      y: "60",
      width: "116",
      height: "90",
      rx: "6",
      fill: "none",
      stroke: "#6f1d3f",
      "stroke-width": "3",
      "stroke-dasharray": "8 5",
    }),
    text(88, "quick"),
    text(112, "brown"),
    text(136, "fox"),
    svg("rect", { x: "100", y: "176", width: "100", height: "8", rx: "4", fill: "#463a33" }),
    ...[0, 1, 2].flatMap((col) =>
      [0, 1].map((row) =>
        svg("rect", {
          x: String(96 + col * 36),
          y: String(206 + row * 34),
          width: "28",
          height: "24",
          rx: "5",
          fill: "#4a3d36",
        }),
      ),
    ),
    svg("path", {
      d: "M214 100 C 240 100 250 92 256 84",
      fill: "none",
      stroke: "#6f1d3f",
      "stroke-width": "3",
    }),
    svg("circle", { cx: "262", cy: "78", r: "14", fill: "#6f1d3f" }),
    svg(
      "text",
      {
        x: "262",
        y: "84",
        "text-anchor": "middle",
        "font-size": "16",
        "font-weight": "800",
        fill: "#fff",
      },
      "3",
    ),
  );
}

/** A receipt printer with a paper coming out. */
export function printerDrawing(): SVGElement {
  return svg(
    "svg",
    { viewBox: "0 0 300 240", class: "ow-drawing", role: "img", "aria-label": "A receipt printer" },
    svg("rect", {
      x: "100",
      y: "30",
      width: "100",
      height: "90",
      rx: "6",
      fill: "#fffdf8",
      stroke: "#b39a7c",
    }),
    svg("line", {
      x1: "114",
      y1: "52",
      x2: "186",
      y2: "52",
      stroke: "#b39a7c",
      "stroke-width": "3",
    }),
    svg("line", {
      x1: "114",
      y1: "68",
      x2: "170",
      y2: "68",
      stroke: "#b39a7c",
      "stroke-width": "3",
    }),
    svg("line", {
      x1: "114",
      y1: "84",
      x2: "180",
      y2: "84",
      stroke: "#b39a7c",
      "stroke-width": "3",
    }),
    svg("rect", { x: "50", y: "110", width: "200", height: "100", rx: "16", fill: "#2a211d" }),
    svg("rect", { x: "72", y: "128", width: "156", height: "10", rx: "5", fill: "#0e0a08" }),
    svg("rect", { x: "70", y: "168", width: "44", height: "20", rx: "5", fill: "#4a3d36" }),
    svg("circle", { cx: "214", cy: "178", r: "7", fill: "#5aa57a" }),
  );
}
